<template>
  <!-- Order Status Card Content -->
  <div 
    v-for="(attachment, index) in messageData.attachments" 
    :key="index" 
    :class="bubbleContentClass"
    :style="{ marginBottom: index < messageData.attachments.length - 1 ? '8px' : '0' }"
  >
    <div 
      class="task-card-content"
      :class="{ 'is-expanded': expandedStates[index], 'is-hovered': hoveredStates[index] }"
      @click="toggleExpand(index)"
      @mouseenter="hoveredStates[index] = true"
      @mouseleave="hoveredStates[index] = false"
    >
      <!-- Order Header -->
      <div class="task-header">
        <div class="task-icon w-[48px] h-[48px]">
          <img :src="require('@/assets/MyPatient/Vector.svg')" alt="Order" class="w-[20px] h-[18px]" />
        </div>
        <div class="task-info">
          <div class="task-title-row">
            <div class="task-title">
              Order ID: {{ getFieldValue(attachment, 'julienBarcode') ? `${getFieldValue(attachment, 'title')} ${getFieldValue(attachment, 'julienBarcode')}` : (getFieldValue(attachment, 'title') || 'Order') }}
            </div>
            <!-- Small status chip when collapsed -->
            <div 
              v-if="!expandedStates[index]"
              class="status-chip-small" 
              :style="{ 
                color: getStatusColor(attachment), 
                backgroundColor: getStatusBackgroundColor(attachment) 
              }"
            >
              {{ getStatusDisplayText(attachment) }}
            </div>
          </div>
          <!-- Order details always visible -->
          <div class="order-details">
            <div class="order-detail-item">Order Placed On: {{ formatSampleOrderTime(getFieldValue(attachment, 'sampleOrderTime')) }}</div>
            <div class="order-detail-item">Amount: $ {{ getFieldValue(attachment, 'amount') || '0.00' }}</div>
            <div class="order-detail-item">Payment Method: {{ getPaymentMethod(attachment) || 'N/A' }}</div>
          </div>
        </div>
      </div>
      
      <!-- Order Status & Actions (only when expanded) -->
      <transition name="expand">
        <div v-if="expandedStates[index]" class="task-content-actions">
          <div class="status-section">
            <div 
              class="status-badge" 
              :style="{ 
                color: getStatusColor(attachment), 
                backgroundColor: getStatusBackgroundColor(attachment) 
              }"
            >
              <span class="status-text">{{ getStatusDisplayText(attachment) }}</span>
            </div>
          </div>
          <div class="task-actions">
            <button class="send-again-btn" @click.stop="seeDetail(attachment)">
              <img
                src="@/assets/MyPatient/detail_icon.svg"
                alt="Details"
                class="w-[18px] h-[18px]"
              />
              <span>See Details</span>
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { 
  getStatusDescription, 
  getStatusDisplayText, 
  getStatusColor, 
  getStatusBackgroundColor 
} from '@/utils/orderStatusUtils';
import { getPortalBaseUrl } from '@/utils/portalUrl';

export default {
  name: "MessageCardOrderStatus",
  props: {
    messageData: {
      type: Object,
      required: true,
    },
    isSent: {
      type: Boolean,
      default: false,
    },
    rc_user_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      paymethodMap:{ 'customerPay': 'Provider Pay', 'patientPayNow': 'Patient Pay Now', 'patientPayLater': 'Patient Pay (Later - send email/text)' },
      expandedStates: {},
      hoveredStates: {}
    }
  },
  computed: {
    isBotMessage() {
      return this.messageData.u._id === 'oafKicny6TymdzNxQ' || 
             this.messageData.u._id === 'EuMKAsjDZTsLc74Kw';
    },
    
    bubbleContentClass() {
      if (this.isBotMessage) {
        return 'bubble-content not-user-bubble task-card-bubble';
      }
      return 'bubble-content task-card-bubble';
    }
  },
  mounted() {
    // Initialize all cards as collapsed
    if (this.messageData?.attachments) {
      this.messageData.attachments.forEach((_, index) => {
        this.expandedStates[index] = false;
        this.hoveredStates[index] = false;
      });
    }
  },
  methods: {
    toggleExpand(index) {
      this.expandedStates[index] = !this.expandedStates[index];
    },
    
    getFieldValue(attachment, fieldTitle) {
      if (!attachment?.fields) { return ''; }
      const field = attachment.fields.find(f => f.title === fieldTitle);
      return field ? field.value : '';
    },
    
    getStatusDisplayText(attachment) {
      const orderStatus = this.getFieldValue(attachment, 'orderStatus');
      return getStatusDisplayText(orderStatus);
    },
    
    getStatusColor(attachment) {
      const orderStatus = this.getFieldValue(attachment, 'orderStatus');
      return getStatusColor(orderStatus);
    },

    getPaymentMethod(attachment){
      const paymentMethod = this.getFieldValue(attachment, 'chargeMethod')
      if(this.paymethodMap[paymentMethod]){
        return this.paymethodMap[paymentMethod]
      }else{
        return 'N/A'
      }
    },
    
    getStatusBackgroundColor(attachment) {
      const orderStatus = this.getFieldValue(attachment, 'orderStatus');
      return getStatusBackgroundColor(orderStatus);
    },
    
    formatSampleOrderTime(dateStr) {
      // Convert from format like "2025-09-03 04:24:27" to "MM-DD-YYYY"
      if (!dateStr) return 'N/A';
      
      const date = new Date(dateStr);
      
      // Format as MM-DD-YYYY in user's local timezone
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      }).replace(/\//g, '-');
    },
    
    async seeDetail(attachment) {
      const barcode = this.getFieldValue(attachment, 'julienBarcode');
      const patientId = this.getFieldValue(attachment, 'patientId');
      const sampleId = this.getFieldValue(attachment, 'sampleId');
      const token = sessionStorage.getItem('token');
      const portalBaseUrl = getPortalBaseUrl();
      const url = `${portalBaseUrl}/#/patient-profile/${patientId}/${barcode}/${sampleId}?jwtToken=${token}`;
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
/* Order Status Card Specific Styles */
.task-card-bubble {
  background-color: #f9fbfd;
  border-radius: 20px;
  padding: 12px 24px;
  width: 100%;
  min-width: 480px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.task-card-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.task-card-content.is-hovered .task-icon {
  background: #004879;
}

.task-card-content.is-hovered .task-icon img {
  filter: brightness(0) invert(1);
}

.task-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.task-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f2f2;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.task-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.task-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.task-title {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #374957;
  letter-spacing: 0.1px;
  flex: 1;
}

.status-chip-small {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 12px;
  border-radius: 12px;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.25px;
  white-space: nowrap;
  flex-shrink: 0;
}

.order-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.order-detail-item {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
}

.task-content-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
}

.status-section {
  width: 100%;
  padding: 8px 0 8px 56px;
  border-bottom: 1px dashed var(--DividerGray, #c4c4c4);
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 48px;
  border-radius: 20px;
}

.status-text {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  text-align: center;
}

.task-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 4px 0;
  width: 100%;
}

.send-again-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #1e4670;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  text-underline-position: from-font;
}

.send-again-btn:hover {
  opacity: 0.8;
}

.send-again-btn span {
  white-space: nowrap;
}

/* Bubble content base styles */
.bubble-content {
  padding: 12px 24px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  position: relative;
  color: #000000;
  border: 1px solid var(--DividerGray, #C4C4C4);
}

.not-user-bubble {
  background-color: #f9fbfd;
  color: #333333;
  border-radius: 20px;
}

/* Expand/Collapse Transition Animations */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.expand-enter-to,
.expand-leave-from {
  max-height: 200px;
  opacity: 1;
  transform: translateY(0);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .task-card-bubble {
    padding: 12px 16px;
    min-width: unset;
  }
  
  .status-section {
    padding-left: 0;
  }
}
</style>
